import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function CTASection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section ref={sectionRef} className="relative py-32 overflow-hidden">
      {/* Background with parallax */}
      <div
        className="absolute inset-0 will-change-transform"
        style={{ transform: `translateY(${scrollY * 0.15}px)` }}
      >
        <img
          src="https://readdy.ai/api/search-image?query=luxurious%20gift%20arrangement%20with%20roses%20candles%20and%20gold%20ribbon%20on%20elegant%20cream%20background%2C%20premium%20boutique%20aesthetic%2C%20warm%20golden%20light%2C%20romantic%20and%20beautiful%2C%20high%20end%20photography%2C%20soft%20pink%20and%20gold%20tones&width=1920&height=800&seq=cta1&orientation=landscape"
          alt="Creative Hub CTA"
          className="w-full h-full object-cover object-top scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-rose-900/85 via-amber-900/70 to-rose-800/80" />
      </div>

      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-rose-300/40"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `particle ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center">
        <div
          className="transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(40px)" }}
        >
          <span className="inline-block text-amber-300 text-xs font-semibold uppercase tracking-widest mb-4">
            — Creative Hub —
          </span>
          <h2
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("cta_title")}
          </h2>
          <h3
            className="text-3xl md:text-4xl font-bold text-amber-300 italic mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("cta_subtitle")}
          </h3>
          <p className="text-white/75 text-lg max-w-xl mx-auto mb-10 leading-relaxed">
            {t("cta_desc")}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/shop"
              className="group flex items-center gap-2 px-8 py-4 bg-white text-amber-800 font-bold rounded-full hover:bg-amber-50 hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shopping-bag-line text-lg"></i>
              {t("cta_btn")}
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200"></i>
            </Link>
            <a
              href="https://wa.me/237678012159?text=Hello%20Creative%20Hub!%20I%27d%20like%20to%20place%20an%20order."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-400 hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              {t("cta_whatsapp")}
            </a>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes particle {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.4; }
          50% { transform: translateY(-30px) scale(1.5); opacity: 0.8; }
        }
      `}</style>
    </section>
  );
}